import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { IconCheck } from './Icon.js';

/**
 * Dropdown menu — the "…" on a table row, the account switcher, filter menus.
 *
 * Radix handles what makes a menu correct and is tedious to write: focus moves
 * into it on open and returns to the trigger on close, typing jumps to a
 * matching item, Escape closes, arrow keys wrap, clicking outside dismisses,
 * and the panel flips or shifts when it would fall off screen.
 *
 * ── PORTALLING AND THEMES ─────────────────────────────────────────────────
 *
 * The panel renders into document.body, outside the React tree. That is why
 * `data-theme` is set on <html> rather than on the app root — with it on the
 * root, every menu in the app would render in light mode on a dark page. It is
 * also why `.cosmos-v2` is applied to the content here: the scoped reset and
 * token styles have to travel with the portal.
 */

export const Menu = RadixMenu.Root;
export const MenuTrigger = RadixMenu.Trigger;

const PANEL = cn(
  'cosmos-v2',
  'z-(--p-z-dropdown) min-w-44 overflow-hidden',
  'rounded-(--overlay-radius) border border-line bg-overlay p-(--overlay-padding)',
  'shadow-(--shadow-overlay)',
  // Grows from wherever Radix placed it — see `.overlay-motion` in
  // tokens/motion.css, which reads Radix's own transform-origin variable so a
  // menu that flipped upwards still animates out of its trigger.
  'overlay-motion',
);

export function MenuContent({
  className,
  align = 'end',
  sideOffset = 6,
  children,
  ...rest
}: RadixMenu.DropdownMenuContentProps) {
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content
        align={align}
        sideOffset={sideOffset}
        // Keeps the panel inside the viewport on a short window, so a menu near
        // the bottom of a long table is still fully reachable.
        collisionPadding={8}
        className={cn(PANEL, className)}
        {...rest}
      >
        {children}
      </RadixMenu.Content>
    </RadixMenu.Portal>
  );
}

const ITEM = cn(
  'relative flex cursor-pointer select-none items-center gap-2',
  'h-(--overlay-item-height) rounded-sm px-2 text-md text-secondary outline-none',
  // Radix sets `data-highlighted` for BOTH hover and keyboard focus, so the two
  // can never look different — a common bug when hover is styled with :hover.
  'data-[highlighted]:bg-hover data-[highlighted]:text-primary',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
);

export function MenuItem({
  className,
  danger = false,
  leading,
  trailing,
  children,
  ...rest
}: RadixMenu.DropdownMenuItemProps & {
  danger?: boolean;
  leading?: ReactNode;
  /**
   * Pinned to the right of the label — a count, a shortcut, a state.
   *
   * A slot rather than "just pass more children": `children` is wrapped in a
   * `flex-1 truncate` span, so anything extra put in there becomes inline
   * content inside a nowrap box. A block-level child then breaks the line and
   * the item renders as two rows. That is exactly how the filter menus shipped
   * with their checkbox stacked above the label.
   */
  trailing?: ReactNode;
}) {
  return (
    <RadixMenu.Item
      className={cn(
        ITEM,
        danger && 'text-danger-fg data-[highlighted]:bg-danger-bg data-[highlighted]:text-danger-fg',
        className,
      )}
      {...rest}
    >
      {leading && <span className="shrink-0 text-tertiary">{leading}</span>}
      {/*
        A menu item clips rather than wraps: the dropdown is a fixed width and a
        wrapping item would change the height of a list the reader is scanning.
        `title` keeps the full text reachable, which is the trade a stat tile
        cannot make because its support line is the definition of a figure.
      */}
      <span title={typeof children === 'string' ? children : undefined} className="flex-1 truncate">
        {children}
      </span>
      {trailing && <span className="shrink-0 tnum text-2xs text-tertiary">{trailing}</span>}
    </RadixMenu.Item>
  );
}

export function MenuCheckboxItem({
  className,
  children,
  trailing,
  ...rest
}: RadixMenu.DropdownMenuCheckboxItemProps & { trailing?: ReactNode }) {
  return (
    <RadixMenu.CheckboxItem className={cn(ITEM, 'pl-7', className)} {...rest}>
      <span className="absolute left-2 grid size-3.5 place-items-center">
        <RadixMenu.ItemIndicator>
          <IconCheck size={13} />
        </RadixMenu.ItemIndicator>
      </span>
      <span title={typeof children === 'string' ? children : undefined} className="flex-1 truncate">
        {children}
      </span>
      {trailing && <span className="shrink-0 tnum text-2xs text-tertiary">{trailing}</span>}
    </RadixMenu.CheckboxItem>
  );
}

export function MenuLabel({ className, children, ...rest }: RadixMenu.DropdownMenuLabelProps) {
  return (
    <RadixMenu.Label
      className={cn('px-2 py-1.5 text-2xs font-semibold uppercase tracking-caps text-tertiary', className)}
      {...rest}
    >
      {children}
    </RadixMenu.Label>
  );
}

export function MenuSeparator({ className, ...rest }: RadixMenu.DropdownMenuSeparatorProps) {
  return (
    <RadixMenu.Separator
      // Negative inset so the rule spans the panel's full width, past its
      // padding — a separator that stops short of the edges reads as an
      // underline on the item above it.
      className={cn('-mx-(--overlay-padding) my-1 h-px bg-subtle', className)}
      {...rest}
    />
  );
}
